import type React from "react"
import { Box, Typography, Paper, Tabs, Tab, List, CircularProgress, Alert, Button, IconButton } from "@mui/material"
import { ArrowBack, FilterList, Refresh } from "@mui/icons-material"
import ConversationItem from "./ConversationItem"
import type { ConversationDto } from "../../types/message"

interface ConversationListProps {
  conversations: ConversationDto[]
  selectedConversationId: number | null
  userProfileId: number | undefined
  loading: boolean
  error: string | null
  tabValue: number
  onTabChange: (event: React.SyntheticEvent, newValue: number) => void
  onConversationClick: (conversation: ConversationDto) => void
  onRetry: () => void
  onBack: () => void
}

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  selectedConversationId,
  userProfileId,
  loading,
  error,
  tabValue,
  onTabChange,
  onConversationClick,
  onRetry,
  onBack,
}) => {
  const filteredConversations = conversations.filter((conversation) => {
    if (tabValue === 0) return true // All
    if (tabValue === 1) return conversation.conversationType === "Solicitud de Alquiler" 
    if (tabValue === 2) return conversation.conversationType === "Solicitud de Compra" 
    return true
  })

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <IconButton onClick={onBack} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" component="h1" sx={{ flexGrow: 1, fontWeight: "bold" }}>
          Mensajes
        </Typography>
        <IconButton>
          <FilterList />
        </IconButton>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" startIcon={<Refresh />} onClick={onRetry}>
              Reintentar
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Paper elevation={2} sx={{ borderRadius: "12px", overflow: "hidden", height: "calc(100vh - 180px)" }}>
        <Tabs
          value={tabValue}
          onChange={onTabChange}
          variant="fullWidth"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: "medium",
              fontSize: "0.95rem",
            },
            "& .Mui-selected": {
              color: "#4caf50",
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#4caf50",
            },
          }}
        >
          <Tab label="Todos" />
          <Tab label="Alquileres" />
          <Tab label="Compras" />
        </Tabs>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress color="success" />
          </Box>
        ) : filteredConversations.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary">
              No tienes conversaciones en esta categoría
            </Typography>
            {!error && (
              <Button variant="outlined" color="success" startIcon={<Refresh />} sx={{ mt: 2 }} onClick={onRetry}>
                Actualizar
              </Button>
            )}
          </Box>
        ) : (
          <List
            sx={{
              width: "100%",
              bgcolor: "background.paper",
              p: 0,
              overflowY: "auto",
              height: "calc(100% - 49px)",
            }}
          >
            {filteredConversations.map((conversation, index) => (
              <ConversationItem
                key={conversation.conversationId}
                conversation={conversation}
                selectedConversationId={selectedConversationId}
                userProfileId={userProfileId}
                onClick={onConversationClick}
                isLast={index === filteredConversations.length - 1}
              />
            ))}
          </List>
        )}
      </Paper>
    </Box>
  )
}

export default ConversationList
